import { collectionQueryOptions, itineraryQueryOptions } from "@mountaya/client-react";
import {
  type EmbedConfig,
  languageSchema,
  type ShareLinkTarget,
  type StudioCapability,
  shareSearchSchema,
} from "@mountaya/studio";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useStore } from "zustand";

import {
  type AuthStrategy,
  mountayaFor,
  organizationQueryOptions,
  userOrganizationsQueryOptions,
  userPersonalSpaceQueryOptions,
  useUserPersonalSpace,
} from "@mountayaapp/api";
import { type OrganizationBranding, ThemeProvider, toBranding } from "@mountayaapp/theme";

import { viewerSessionQueryOptions } from "../auth/rules/viewerSession";
import { env } from "../env";
import { summarizeMemberships } from "../iam/shared/rules/organizationResolution";
import { useAppMapLanguage } from "../shared/hooks/useAppMapLanguage";
import { ElementsProvider } from "../shared/providers/ElementsProvider";
import { personalSpaceReader } from "../shared/rules/personalSpaceReader";
import type { OwnerOverlayRules } from "../studio/layers/hooks/studioConfig";
import { EmbeddedStudioProvider } from "../studio/providers/EmbeddedStudioProvider";
import { resolveHostCopy } from "../studio/rules/hostCopy";
import { SharedStudio } from "../studio/share/components/SharedStudio";
import { ShareInvalidLink } from "../studio/share/components/ShareInvalidLink";
import { ShareUnpublished } from "../studio/share/components/ShareUnpublished";

/**
 * SHARE_CAPABILITIES is the read-only grant the share surface runs with: the embed
 * display subset PLUS "share".
 *
 * The read-only view verbs the side nav drives (segment/waypoint select, itinerary
 * emphasis, map hover) moved DOWN to "embedding", because the published viewer's own
 * surfaces drive them and the public embed grants "embedding" alone — so they were inert
 * there while the camera still flew. `share` therefore gates no tool today; it is kept
 * because the two audiences are not the same audience, and the first verb that only a
 * share-link visitor may run has somewhere to go. Hoisted so the prop is a stable
 * reference across renders.
 */
const SHARE_CAPABILITIES: readonly StudioCapability[] = ["embedding", "share"];

/**
 * ShareSession is the resolved per-request share identity carried in the ROUTER
 * CONTEXT (never Zustand): the resource-scoped share auth strategy every read (data +
 * tiles) authenticates with, the org branding (null when the org did not resolve), the
 * shared TARGET (an itinerary or a collection, whichever the token was minted for) and
 * its name, the resolved display language the view config carries, the share token itself
 * (so the nav can present a copyable share link without re-reading the URL), and whether
 * the publisher holds the white-label entitlement. The
 * camera zoom range is NOT resolved here — the share surface derives it from the ROOT
 * org (like /studio), so a misconfigured publisher org can never clamp the map.
 */
interface ShareSession {
  auth: AuthStrategy;
  branding: OrganizationBranding | null;
  language: EmbedConfig["preferredLanguage"];
  name: string;
  /**
   * overlayRules is what the PUBLISHING organization's safety rules say about the overlays the
   * link draws: its overlay allowlist and its overlay time control, which the Tile API holds
   * every overlay tile asked for under the link to. Null when the org did not resolve, so the
   * view offers no overlay rather than ones whose tiles are refused.
   */
  overlayRules: OwnerOverlayRules | null;
  shareToken: string;
  target: ShareLinkTarget;
  /**
   * whiteLabel is whether the PUBLISHING organization may take the Mountaya mark off what
   * this surface renders. It rides here rather than being derived from the viewer because
   * it belongs to the publisher, not to whoever opened the link — an anonymous visitor and
   * a signed-in one see the same mark, or the same absence of it. False when the org did
   * not resolve, so the mark stays on.
   */
  whiteLabel: boolean;
}

/**
 * ShareRouteContext is what the share route's beforeLoad merges into the router
 * context: the resolved share session, or null when the link cannot be resolved (a
 * missing/malformed token, a token the shared resource read rejects, or a resource
 * that belongs to no organization), plus whether
 * the ITINERARY behind a resolved link has ever been published. A collection link is
 * never unpublished — a collection has no publication — so that flag stays false for
 * one, and the not-yet-published screen stays an itinerary answer. The component
 * renders the invalid-link screen for null, the not-yet-published screen when the link
 * resolves to unpublished work, and the share surface otherwise. The best-effort viewer whoami
 * seeds the identity store, so the leaf components read the signed-in flag from there
 * (useIsAuthenticated), never from this context.
 */
interface ShareRouteContext {
  share: ShareSession | null;
  unpublished: boolean;
}

/**
 * Route is /studio/share: the PUBLIC read-only share surface. The trailing underscore
 * on the `studio_` segment un-nests it from the _app layout, so it inherits neither the
 * auth guard nor the app shell — an anonymous visitor renders it directly, and the root
 * beforeLoad skips the Ory whoami for this path.
 *
 * beforeLoad first guards the params (a missing target id or a token that is not
 * `share_`-prefixed degrades to the invalid-link screen before any network call). It
 * then primes the target's cache under the share auth strategy — the read rides the
 * X-Share-Token header, so a disabled / rotated / foreign token 401/403s (and an unknown
 * itinerary 404s) here and doubles as the authorization probe; on success the cache
 * seeds studio.itinerary.activate. Alongside it, in parallel, runs a best-effort viewer
 * whoami — a signed-in visitor's own membership layers the same feature gating /studio applies
 * and hides the "Open Mountaya" call-to-action, without ever becoming the read
 * credential. A best-effort org read over the same token then carries the branding and
 * the org language into the surface (a failure leaves neutral fallbacks); the camera
 * zoom comes from the ROOT org, resolved inside the surface, not this publisher org.
 * validateSearch is the two-field share schema from core.
 */
export const Route = createFileRoute("/studio_/share")({
  beforeLoad: async ({ context, search }): Promise<ShareRouteContext> => {
    /**
     * seedViewer runs the best-effort viewer whoami. A signed-in visitor's own answers layer
     * the feature gating the share view applies (the terrain analysis follows their personal
     * space's answer, Street View their memberships) and hide the "Open Mountaya" CTA; the
     * viewer's session NEVER becomes the
     * read credential — every resource read below still rides the share token. The
     * whoami is a cached query (not ensureSession, which reset()s the store on a null
     * session and would wipe the shared-org seed) so a preload plus the load resolve it
     * once from cache rather than re-firing it; it resolves null for an anonymous
     * visitor (a 401) or a failed probe.
     *
     * It swallows its own failures rather than rejecting, which is what lets the caller
     * race it against the target read without a rejection from one aborting the other.
     */
    const seedViewer = async (): Promise<void> => {
      try {
        const viewer = await context.queryClient.ensureQueryData(viewerSessionQueryOptions());
        if (!viewer) {
          return;
        }

        context.identityStore.getState().setSession(viewer);

        // Membership parity with /studio: fetch the VIEWER's OWN organizations over their
        // Ory cookie so a member reads as one here too, exactly as the _app guard derives
        // it. A lookup failure degrades to no membership. This viewer seed settles before
        // the shared-org seed below, so the shared org's display language still wins the
        // preferences.language collision. Beside it, the viewer's personal space's answer,
        // which the surface's terrain analysis follows (`publishedSurfaceViewer`): read here so
        // a signed-in visitor meets the analysis on first paint, and a failure leaves it
        // withheld until the route's own read lands.
        const [organizations] = await Promise.all([
          context.queryClient
            .ensureQueryData(
              userOrganizationsQueryOptions({ baseUrl: env.apiBaseUrl }, viewer.userId),
            )
            .catch(() => []),
          context.queryClient
            .ensureQueryData(
              userPersonalSpaceQueryOptions({ baseUrl: env.apiBaseUrl }, viewer.userId),
            )
            .catch(() => undefined),
        ]);

        context.identityStore
          .getState()
          .setMemberships(summarizeMemberships(organizations, env.rootOrganizationId));
      } catch {
        // No active session (or the whoami failed): the viewer is anonymous and the share
        // surface renders the full read-only view, unchanged.
      }
    };

    const {
      collection_id: collectionId,
      itinerary_id: itineraryId,
      share_token: shareToken,
    } = search;

    // A share link must carry exactly one target and a well-formed share token; a
    // missing or wrong-shaped token degrades before any network call and never reaches
    // the API as a credential. The search schema has already dropped both ids when a
    // link named two, so "neither" here covers that case too.
    const target: ShareLinkTarget | null = collectionId
      ? { id: collectionId, kind: "collection" }
      : itineraryId
        ? { id: itineraryId, kind: "itinerary" }
        : null;

    if (!target || !shareToken?.startsWith("share_")) {
      // The invalid-link screen is viewer-aware too, so the seed still runs — it is just
      // the only thing left to run.
      await seedViewer();

      return { share: null, unpublished: false };
    }

    const auth: AuthStrategy = { kind: "share", shareToken };
    // The client the prefetch reads through, and the reason the surface beneath it lands on
    // the same cache entries: every studio key is scoped to the credential, and this is the
    // same credential `EmbeddedStudioProvider` declares for the subtree. `mountayaFor`
    // caches per credential, so this is the instance the provider resolves too.
    const client = mountayaFor({ auth, baseUrl: env.apiBaseUrl });

    try {
      // Priming the target's cache doubles as the authorization probe: the read rides
      // the share token, so a token that does not authorize this resource fails here
      // and degrades to the invalid screen. For an itinerary it also seeds the cache
      // for the activate tool the reused EmbeddedStudio dispatches; for a collection it
      // resolves the name the page and its nav are titled from.
      //
      // A collection is never unpublished — it has no publication — so only the
      // itinerary branch can answer the not-yet-published screen.
      //
      // It races the viewer seed rather than following it. The two answer unrelated
      // questions — one authorizes the token, the other only colours the surface with
      // the visitor's own membership — and running them in series spent a serial round trip on
      // the one surface that is reached cold, from a link, by someone with no session to
      // warm anything. Promise.all attaches a handler to both immediately, so the
      // target's rejection lands in this catch instead of escaping unhandled. This
      // beforeLoad runs on PRELOAD (a hover) as well as a visit; both halves are cached
      // reads and neither mutates, so racing them stays safe there too.
      const [resolved] = await Promise.all([
        target.kind === "collection"
          ? context.queryClient
              .ensureQueryData(collectionQueryOptions(client, target.id))
              .then(({ data: collection }) => ({
                name: collection.name,
                organizationId: collection.organization_id,
                unpublished: false,
              }))
          : context.queryClient
              .ensureQueryData(itineraryQueryOptions(client, target.id))
              .then(({ data: itinerary }) => ({
                name: itinerary.name,
                organizationId: itinerary.organization_id,
                unpublished: itinerary.status !== "published",
              })),
        seedViewer(),
      ]);

      // A share link belongs to an organization: the API resolves no link to a resource
      // that belongs to none, so a read answering one is a link this surface does not
      // show either.
      if (!resolved.organizationId) {
        return { share: null, unpublished: false };
      }

      let branding: OrganizationBranding | null = null;
      let language: EmbedConfig["preferredLanguage"] = "en";

      // Best-effort org read over the SAME share token — the backend serves the bare
      // embed org shape under a share credential — for the branding and the org's
      // preferred language. A failure leaves the neutral branding fallback; the target
      // already validated. The camera zoom range is NOT read here: the share surface
      // clamps to the ROOT org (like /studio), not this publisher org. Both target
      // kinds carry the owning organization, so a shared collection is branded and
      // localized exactly like a shared itinerary. The same read answers whether the
      // publisher may take the Mountaya mark off what this surface renders.
      let whiteLabel = false;
      let overlayRules: OwnerOverlayRules | null = null;
      try {
        const organization = await context.queryClient.ensureQueryData(
          organizationQueryOptions({ auth, baseUrl: env.apiBaseUrl }, resolved.organizationId),
        );

        branding = toBranding(organization.settings.theme?.colors?.ui);
        whiteLabel = organization.white_label;
        const safetyRules = organization.settings.safety_rules;
        overlayRules = safetyRules
          ? {
              withOverlays: safetyRules.with_overlays,
              withOverlaysControl: safetyRules.with_overlays_control,
            }
          : null;
        language = languageSchema
          .catch("en")
          .parse(organization.settings.default_props?.preferred_language);
        context.identityStore.getState().setOrganization(organization.id);
        context.identityStore.getState().setPreferences({ language });
      } catch {
        // The org failed to load; keep the neutral fallbacks and render the share view.
      }

      return {
        share: {
          auth,
          branding,
          language,
          name: resolved.name,
          overlayRules,
          shareToken,
          target,
          whiteLabel,
        },
        // A link to work its owner has never published resolves, but there is nothing
        // published behind it yet. That is a different answer from a dead link — this
        // one will start working the moment they publish — so it gets its own screen
        // rather than the deliberately vague invalid one.
        unpublished: resolved.unpublished,
      };
    } catch {
      // The target read failed (invalid / disabled / rotated token, or a token that does
      // not authorize this resource): degrade to the invalid-link screen.
      return { share: null, unpublished: false };
    }
  },
  component: ShareRoute,
  validateSearch: shareSearchSchema,
});

function ShareRoute() {
  // identityStore is the module-scoped store threaded through the router context (the
  // same instance beforeLoad seeded via setOrganization). share is beforeLoad's result.
  const { identityStore, share, unpublished } = Route.useRouteContext();
  const copy = resolveHostCopy(useAppMapLanguage());
  // The visitor's own memberships, which `beforeLoad` seeded from their session. The studio's
  // viewer beneath answers the overlays for the organization that published the link, so the
  // one gate that stays the visitor's, Street View, is read here: it follows the person, held
  // by a member of an organization whose agreement includes Map Studio.
  const holdsStudio = useStore(identityStore, (state) => state.studioOrganizationIds.length > 0);
  // The visitor's own personal space's answer, which the terrain analysis follows: the server
  // hands a share-link reader the analysis by their own access. Kept read (and recovered) for a
  // signed-in visitor; an anonymous one has no user to ask about, and the reader answers nothing.
  const userId = useStore(identityStore, (state) => state.userId);
  useUserPersonalSpace({ baseUrl: env.apiBaseUrl }, userId);
  const queryClient = useQueryClient();
  // Once per cache and store, so the viewer the surface builds over it keeps its identity.
  const personalSpace = useMemo(
    () => personalSpaceReader(queryClient, identityStore),
    [identityStore, queryClient],
  );

  useEffect(() => {
    // Token-in-URL hygiene: keep the share page (its URL carries the token) out of
    // search indexes. The SPA has no per-route document head, so append the meta
    // imperatively and remove it on unmount; also title the tab for the itinerary.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    const previousTitle = document.title;
    document.title = share?.name ?? copy.shareLink.invalidTitle;

    return () => {
      meta.remove();
      document.title = previousTitle;
    };
  }, [copy, share]);

  // ElementsProvider wraps the WHOLE route, not just the map. `EmbeddedStudio` mounts one
  // of its own, but only around the map pane — so every published surface this route renders
  // BESIDE that pane (the read-only side nav, its share dialog, the invalid-link and
  // not-yet-published screens) resolved the three glyph seams outside any provider and drew
  // the bundled Lucide fallbacks: the six brand marks in the share row all came out as the
  // same generic share node, and the activity badge in the dialog header drew NOTHING at all,
  // because the fallback marker set answers no `activity-*` name and the badge renders null
  // rather than an empty chip. The route breaks out of `_app`, which is where the rest of the
  // application mounts this, so the seams have to be supplied here.
  //
  // Not `embedded`: that flag only selects the Street View handle's wording ("onto the
  // itinerary" rather than "onto the map"), which belongs to a map control — and those sit
  // under `EmbeddedStudio`'s own nested provider, which states it and still inherits these
  // tables untouched.
  return (
    <ElementsProvider>
      {share === null ? (
        <ShareInvalidLink />
      ) : unpublished ? (
        <ShareUnpublished />
      ) : (
        // The share subtree pins colorScheme="light" to match the app studio (which is always
        // light) — unlike the embed, which honors the publisher's chosen light/dark scheme. It
        // applies the org branding when resolved and grants the read-only embed + share
        // capabilities to the reused EmbeddedStudio.
        <ThemeProvider branding={share.branding} colorScheme="light">
          <EmbeddedStudioProvider
            auth={share.auth}
            capabilities={SHARE_CAPABILITIES}
            identityStore={identityStore}
            personalSpace={personalSpace}
            whiteLabel={share.whiteLabel}
          >
            <SharedStudio
              auth={share.auth}
              holdsStudio={holdsStudio}
              language={share.language}
              name={share.name}
              overlayRules={share.overlayRules}
              shareToken={share.shareToken}
              target={share.target}
            />
          </EmbeddedStudioProvider>
        </ThemeProvider>
      )}
    </ElementsProvider>
  );
}
